import { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
    Box,
    Typography,
    IconButton,
    Button,
    Skeleton,
} from "@mui/material";
import { ArrowBack, ArrowForwardIos, ApartmentOutlined } from "@mui/icons-material";
import Icons from "@/assets";
import { SectionContribution, NavigationBuilding, NavigationEntryPoint } from "@/types/common";
import useApiClient from "@/api/Client";
import ENDPOINTS from "@/api/endpoints";
import { toast } from "react-toastify";
import { getApiErrorMessage } from "@/utils/helper";
import { getIconForSectionType } from "@/utils/buildingIcons";

const CATEGORY_META = {
    entrance: { title: "Entrance" },
    exit: { title: "Exit" },
} as const;

// Fallback shown only for legacy contributions with no `buildings` data.
const legacySections = [
    {
        key: "entrance",
        title: "Entrance",
        subtitle:
            "Step-free entry, ramp at entrance, automatic/manual doors, handrails.",
        icon: Icons.Entrance,
        iconBg: "linear-gradient(135deg, #FF8A80 0%, #FF1744 100%)",
    },
    {
        key: "exit",
        title: "Exit",
        subtitle: "Safe exit routes? Barriers?",
        icon: Icons.Exit,
        iconBg: "linear-gradient(135deg, #69F0AE 0%, #00C853 100%)",
    },
];

type NavigationData = {
    contributionDetails?: Record<string, SectionContribution>;
    buildings?: NavigationBuilding[];
    navigation_history_id?: string | number;
};

const NavigationBreakdown = () => {
    const navigate = useNavigate();
    const { place_id } = useParams();
    const location = useLocation();
    const { state } = location;
    const { place_name, preservedNavData } = state || {};
    const { api } = useApiClient();

    const [loading, setLoading] = useState(true);
    const [navData, setNavData] = useState<NavigationData | null>(null);
    const [selectedBuildingId, setSelectedBuildingId] = useState<number | null>(null);

    useEffect(() => {
        if (preservedNavData) {
            setNavData(preservedNavData);
            setLoading(false);
            return;
        }

        const fetchNavigationData = async () => {
            if (!place_id) return;
            try {
                const res = await api.get(
                    `${ENDPOINTS.NAVIGATE.START(place_id)}?place_id=${place_id}`,
                );
                const json = res?.data;
                if (json?.status === 200 && json?.data) {
                    setNavData(json.data);
                    navigate(location.pathname, {
                        replace: true,
                        state: { ...(state || {}), preservedNavData: json.data },
                    });
                } else {
                    toast.error(json?.message || "Failed to start navigation.");
                }
            } catch (err) {
                console.error("Navigate error:", err);
                toast.error(getApiErrorMessage(err, "Something went wrong. Please try again."));
            } finally {
                setLoading(false);
            }
        };

        fetchNavigationData();
    }, [place_id, api, preservedNavData, navigate, location.pathname]);

    const handleSectionPress = (sectionKey: string) => {
        if (!navData) return;
        navigate(`/navigation/${place_id}/detail`, {
            state: {
                contributionDetails: navData.contributionDetails,
                navigation_history_id: navData.navigation_history_id,
                place_name,
                activeTab: sectionKey,
            },
        });
    };

    const buildings = navData?.buildings ?? [];
    const hasBuildings = buildings.length > 0;
    const selectedBuilding = buildings.find((b) => b.building_id === selectedBuildingId) ?? null;

    const handleBuildingPress = (buildingId: number) => {
        setSelectedBuildingId(buildingId);
    };

    const handleEntryPointPress = (ep: NavigationEntryPoint) => {
        if (!navData || !selectedBuilding) return;
        navigate(`/navigation/${place_id}/detail`, {
            state: {
                buildings: navData.buildings,
                initialBuildingId: selectedBuilding.building_id,
                initialEntryPointId: ep.entry_point_id,
                navigation_history_id: navData.navigation_history_id,
                place_name,
            },
        });
    };

    const handleHeaderBack = () => {
        if (hasBuildings && selectedBuildingId !== null) {
            setSelectedBuildingId(null);
        } else {
            navigate(-1);
        }
    };

    const headerTitle = hasBuildings
        ? selectedBuilding
            ? `${selectedBuilding.building_name}`
            : "Choose a Building"
        : "Navigation Breakdowns";

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                height: "100vh",
                backgroundColor: "#F5F5F5",
                overflow: "hidden",
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    px: "4%",
                    py: "3.5%",
                    backgroundColor: "#F5F5F5",
                    gap: "2%",
                    boxShadow: "0px 1px 3px rgba(0,0,0,0.08)",
                    position: "sticky",
                    top: 0,
                    zIndex: 10,
                }}
            >
                <IconButton onClick={handleHeaderBack} size="small">
                    <ArrowBack />
                </IconButton>
                <Typography
                    sx={{ fontSize: { xs: "4vw", sm: 17 }, fontWeight: 700, color: "#191919" }}
                >
                    {headerTitle}
                </Typography>
            </Box>

            {/* Cards */}
            <Box sx={{ flex: 1, p: "4%", display: "flex", flexDirection: "column", gap: "3%", overflowY: "auto" }}>
                {loading ? (
                    [1, 2].map((item) => (
                        <Box
                            key={item}
                            sx={{
                                backgroundColor: "#fff",
                                borderRadius: "16px",
                                p: "4%",
                                display: "flex",
                                alignItems: "center",
                                gap: "3%",
                                boxShadow: "0px 2px 8px rgba(0,0,0,0.07)",
                            }}
                        >
                            <Skeleton variant="rounded" sx={{ width: { xs: "12vw", sm: 50 }, height: { xs: "12vw", sm: 50 }, borderRadius: "12px" }} />
                            <Box sx={{ flex: 1 }}>
                                <Skeleton variant="text" width="40%" height={24} sx={{ mb: "1%" }} />
                                <Skeleton variant="text" width="80%" height={18} />
                                <Skeleton variant="text" width="60%" height={18} />
                            </Box>
                            <ArrowForwardIos sx={{ color: "#626262", fontSize: 16 }} />
                        </Box>
                    ))
                ) : hasBuildings && !selectedBuilding ? (
                    buildings.map((building) => (
                        <Box
                            key={building.building_id}
                            onClick={() => handleBuildingPress(building.building_id)}
                            sx={{
                                backgroundColor: "#fff",
                                borderRadius: "16px",
                                p: "4%",
                                display: "flex",
                                alignItems: "center",
                                gap: "3%",
                                boxShadow: "0px 2px 8px rgba(0,0,0,0.07)",
                                cursor: "pointer",
                                transition: "transform 0.15s ease, box-shadow 0.15s ease",
                                "&:active": {
                                    transform: "scale(0.98)",
                                    boxShadow: "0px 1px 4px rgba(0,0,0,0.1)",
                                },
                            }}
                        >
                            <Box
                                sx={{
                                    width: { xs: "12vw", sm: 50 },
                                    height: { xs: "12vw", sm: 50 },
                                    borderRadius: "12px",
                                    background: "#F0DEFF",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    flexShrink: 0,
                                }}
                            >
                                <ApartmentOutlined sx={{ color: "#6f2fa8" }} />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <Typography
                                    sx={{
                                        fontWeight: 700,
                                        fontSize: { xs: "3.88vw", sm: 16 },
                                        color: "#191919",
                                    }}
                                >
                                    {building.building_name}
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: { xs: "3.1vw", sm: 13 },
                                        color: "#666",
                                        lineHeight: 1.4,
                                    }}
                                >
                                    {(() => {
                                        const entranceCount = building.entryPoints.filter((ep) => ep.point_category === "entrance").length;
                                        const exitCount = building.entryPoints.filter((ep) => ep.point_category === "exit").length;
                                        const parts: string[] = [];
                                        if (entranceCount > 0) parts.push(`${entranceCount} Entrance${entranceCount !== 1 ? "s" : ""}`);
                                        if (exitCount > 0) parts.push(`${exitCount} Exit${exitCount !== 1 ? "s" : ""}`);
                                        return parts.length > 0 ? parts.join(" • ") : "No entry points";
                                    })()}
                                </Typography>
                            </Box>
                            <ArrowForwardIos sx={{ color: "#626262", fontSize: 16 }} />
                        </Box>
                    ))
                ) : hasBuildings && selectedBuilding ? (
                    selectedBuilding.entryPoints.map((ep) => {
                        const meta = CATEGORY_META[ep.point_category];
                        return (
                            <Box
                                key={ep.entry_point_id}
                                onClick={() => handleEntryPointPress(ep)}
                                sx={{
                                    backgroundColor: "#fff",
                                    borderRadius: "16px",
                                    p: "4%",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "3%",
                                    boxShadow: "0px 2px 8px rgba(0,0,0,0.07)",
                                    cursor: "pointer",
                                    transition: "transform 0.15s ease, box-shadow 0.15s ease",
                                    "&:active": {
                                        transform: "scale(0.98)",
                                        boxShadow: "0px 1px 4px rgba(0,0,0,0.1)",
                                    },
                                }}
                            >
                                <Box
                                    sx={{
                                        width: { xs: "12vw", sm: 50 },
                                        height: { xs: "12vw", sm: 50 },
                                        borderRadius: "12px",
                                        background: "#F0DEFF",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        flexShrink: 0,
                                    }}
                                >
                                    {getIconForSectionType(
                                        ep.section_type ?? ep.entry_name,
                                        { xs: "5.5vw", sm: 24 },
                                    )}
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                    <Typography
                                        sx={{
                                            fontWeight: 700,
                                            fontSize: { xs: "3.88vw", sm: 16 },
                                            color: "#191919",
                                            mb: "1%",
                                        }}
                                    >
                                        {ep.entry_name}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontSize: { xs: "3.1vw", sm: 13 },
                                            color: "#666",
                                            lineHeight: 1.4,
                                        }}
                                    >
                                        {meta.title}
                                    </Typography>
                                </Box>
                                <ArrowForwardIos sx={{ color: "#626262", fontSize: 16 }} />
                            </Box>
                        );
                    })
                ) : (
                    legacySections.map((section) => (
                        <Box
                            key={section.key}
                            onClick={() => handleSectionPress(section.key)}
                            sx={{
                                backgroundColor: "#fff",
                                borderRadius: "16px",
                                p: "4%",
                                display: "flex",
                                alignItems: "center",
                                gap: "3%",
                                boxShadow: "0px 2px 8px rgba(0,0,0,0.07)",
                                cursor: "pointer",
                                transition: "transform 0.15s ease, box-shadow 0.15s ease",
                                "&:active": {
                                    transform: "scale(0.98)",
                                    boxShadow: "0px 1px 4px rgba(0,0,0,0.1)",
                                },
                            }}
                        >
                            {/* Icon */}
                            <Box
                                sx={{
                                    width: { xs: "12vw", sm: 50 },
                                    height: { xs: "12vw", sm: 50 },
                                    borderRadius: "12px",
                                    background: section.iconBg,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    flexShrink: 0,
                                    p: "0%",
                                }}
                            >
                                <Box
                                    component="img"
                                    src={section.icon}
                                    alt={section.title}
                                    sx={{ width: "100%", height: "100%", objectFit: "contain" }}
                                />
                            </Box>

                            {/* Text */}
                            <Box sx={{ flex: 1 }}>
                                <Typography
                                    sx={{
                                        fontWeight: 700,
                                        fontSize: { xs: "3.88vw", sm: 16 },
                                        color: "#191919",
                                        mb: "1%",
                                    }}
                                >
                                    {section.title}
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: { xs: "3.1vw", sm: 13 },
                                        color: "#666",
                                        lineHeight: 1.4,
                                    }}
                                >
                                    {section.subtitle}
                                </Typography>
                            </Box>
                            <ArrowForwardIos sx={{ color: "#626262", fontSize: 16 }} />
                        </Box>
                    ))
                )}
            </Box>

            {/* Start Button area (Pinned to bottom) - legacy fallback only; the
                building/entry-point flow is driven entirely by card taps above. */}
            {!hasBuildings && !loading && (
            <Box
                sx={{
                    px: "5%",
                    pb: "8%",
                    pt: "4%",
                    mt: "auto",
                    position: "fixed",
                    width: "100%",
                    bottom: 0,
                    zIndex: 10,
                    background: "transparent",
                    pointerEvents: "none",
                }}
            >
                <Button
                    fullWidth
                    variant="contained"
                    onClick={() => handleSectionPress("entrance")}
                    sx={{
                        pointerEvents: "auto", 
                        borderRadius: "30px",
                        py: "3.5%",
                        fontSize: { xs: "4vw", sm: 16 },
                        fontWeight: 700,
                        background: "linear-gradient(135deg, #9443DF 0%, #6B21A8 100%)",
                        boxShadow: "0px 4px 16px rgba(148,67,223,0.35)",
                        textTransform: "none",
                        "&:hover": {
                            background: "linear-gradient(135deg, #7C3AED 0%, #581C87 100%)",
                        },
                    }}
                >
                    Start
                </Button>
            </Box>
            )}
        </Box>
    );
};

export default NavigationBreakdown;
